import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";

const fmt4 = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n.toFixed(4) : "";
};

const fmt2 = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

export default function BonTransferPrint() {
  const { id } = useParams();
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [doc, setDoc] = useState(null);
  const [gestiuneName, setGestiuneName] = useState("");
  const [gestiune2Name, setGestiune2Name] = useState("");
  const [contactName, setContactName] = useState("");
  const [partNames, setPartNames] = useState({});

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setBusy(true);
      setErr("");
      try {
        const d = await authFetch(`/bon_transfer/${id}`);
        if (cancelled) return;
        setDoc(d);

        // Fetch Gestiune Sursă Name
        if (d.bxgestiune_id) {
          try {
            const g = await authFetch(`/bxgestiune/${d.bxgestiune_id}`);
            if (!cancelled && g) {
              setGestiuneName(g.denumire || g.bxnume || g.name || `Gestiune #${d.bxgestiune_id}`);
            }
          } catch (e) {
            console.warn(e);
          }
        }

        // Fetch Gestiune Destinație Name
        if (d.bxgestiune2_id) {
          try {
            const g = await authFetch(`/bxgestiune/${d.bxgestiune2_id}`);
            if (!cancelled && g) {
              setGestiune2Name(g.denumire || g.bxnume || g.name || `Gestiune #${d.bxgestiune2_id}`);
            }
          } catch (e) {
            console.warn(e);
          }
        }

        // Fetch Contact Name
        if (d.contact_id) {
          try {
            const c = await authFetch(`/contacte/${d.contact_id}`);
            if (!cancelled && c) {
              setContactName(c.bxdenumire || c.name || `Contact #${d.contact_id}`);
            }
          } catch (e) {
            console.warn(e);
          }
        }

        // Map lines & Fetch Part Names
        const outs = d.linii_out || [];
        const ins = d.linii || [];
        const partIds = new Set(ins.map(i => i.bxpart_id).filter(Boolean));
        
        const namesMap = {};
        for (const pId of partIds) {
          try {
            const p = await authFetch(`/bxpart/${pId}`);
            if (p) {
              namesMap[pId] = `${p.cod || ""} ${p.denumire || ""}`.trim();
            }
          } catch (e) {
            namesMap[pId] = `Part #${pId}`;
          }
        }

        if (!cancelled) {
          setPartNames(namesMap);
          // Print dialog
          setTimeout(() => window.print(), 300);
        }
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (busy)
    return (
      <div className="p-3">
        <Spinner animation="border" />
      </div>
    );
  if (err)
    return (
      <div className="p-3">
        <Alert variant="danger">{err}</Alert>
      </div>
    );
  if (!doc) return null;

  const outs = doc.linii_out || [];
  const ins = doc.linii || [];
  
  const mappedLines = outs.map((out, idx) => {
    const correspondingIn = ins[idx];
    return {
      id: out.id,
      bxpart_id: correspondingIn?.bxpart_id ?? null,
      qty: out.bxqtyscazut ?? 0,
      price: correspondingIn?.bxpart_regprice ?? 0,
    };
  });

  const totalValoare = mappedLines.reduce((acc, ln) => {
    return acc + (ln.qty * ln.price);
  }, 0);

  return (
    <div className="print-a4">
      {/* Header */}
      <div className="print-header">
        <div>
          <div className="print-title">BON DE TRANSFER</div>
          <div className="muted mt-2">
            Nr: <b>{doc.bxdoc_no || "-"}</b> &nbsp;|&nbsp; Data:{" "}
            <b>{String(doc.bxdoc_date || "").slice(0, 10) || "-"}</b>
          </div>
        </div>
        <div className="text-end muted">
          <div>
            Gestiune Sursă: <b>{gestiuneName || `#${doc.bxgestiune_id}`}</b>
          </div>
          <div>
            Gestiune Destinație: <b>{gestiune2Name || `#${doc.bxgestiune2_id}`}</b>
          </div>
          {doc.observatii && (
            <div className="mt-1">
              Observații: <i>{doc.observatii}</i>
            </div>
          )}
        </div>
      </div>

      {/* Given To / Personal */}
      <div className="print-box">
        <div className="print-box-title">Predat la / Personal</div>
        <div>
          <b>{contactName || `Contact #${doc.contact_id}`}</b>
        </div>
      </div>

      {/* Table lines */}
      <table className="print-table">
        <thead>
          <tr>
            <th style={{ width: "6%" }}>#</th>
            <th>Articol</th>
            <th style={{ width: "20%" }} className="text-end">
              Cantitate
            </th>
            <th style={{ width: "20%" }} className="text-end">
              Preț stoc
            </th>
            <th style={{ width: "22%" }} className="text-end">
              Valoare stoc
            </th>
          </tr>
        </thead>
        <tbody>
          {mappedLines.map((ln, idx) => {
            const value = ln.qty * ln.price;
            return (
              <tr key={ln.id || idx}>
                <td className="muted">{idx + 1}</td>
                <td>
                  <b>{partNames[ln.bxpart_id] || `Articol #${ln.bxpart_id}`}</b>
                </td>
                <td className="text-end">{fmt4(ln.qty)}</td>
                <td className="text-end">{fmt4(ln.price)}</td>
                <td className="text-end">{fmt2(value)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totals */}
      <div className="print-totals">
        <div className="muted"></div>
        <div className="totals-box">
          <div className="row2 grand">
            <div>Valoare Transfer</div>
            <div className="text-end">
              <b>{fmt2(totalValoare)}</b>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="print-footer muted">
        <div className="grid-2">
          <div>Predat (Gestiune Sursă)</div>
          <div>Primit (Gestiune Destinație)</div>
        </div>
        <div className="grid-2 mt-4">
          <div className="line" style={{ maxWidth: "200px" }}></div>
          <div className="line" style={{ maxWidth: "200px" }}></div>
        </div>
      </div>

      {/* Print Button (Screen only) */}
      <div className="no-print mt-4">
        <button className="btn btn-secondary" onClick={() => window.print()}>
          Print
        </button>
      </div>
    </div>
  );
}
